import { h, render, reactive, computed, nextTick } from 'vue';
import LoadingEl from '../components/Loading.vue';

export type LoadingProps = {
	tip?: string;
	color?: string;
	textColor?: string;
	vertical?: boolean;
	delay?: number;
	background?: string;
	zIndex?: number;
	singonly?: boolean;
};

const defaultProps: LoadingProps = {
	tip: '加载中...',
	color: '#36ffcd',
	vertical: true,
	background: 'rgba(0, 0, 0, 0.4)',
	zIndex: 1999,
};

// 全局单例状态
const globalState = reactive({
	visible: false,
	props: { ...defaultProps },
	showTimer: null as ReturnType<typeof setTimeout> | null,
	hideTimer: null as ReturnType<typeof setTimeout> | null,
});

let globalContainer: HTMLElement | null = null;

/**
 * 获取网络连接类型
 * @returns 网络类型: '4g' | '3g' | '2g' | 'slow-2g' | 'unknown'
 */
function getNetworkType(): string {
	if (typeof navigator === 'undefined') return 'unknown';
	const conn = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
	return conn?.effectiveType || 'unknown';
}

/**
 * 根据网络类型获取加载超时时间(毫秒)
 * @param networkType 网络类型
 * @returns 超时时间(ms)
 */
function getLoadingTimeout(networkType: string): number {
	switch (networkType) {
		case 'slow-2g':
			return 8000; // 8s
		case '2g':
			return 5000; // 5s
		case '3g':
			return 3000; // 3s
		case '4g':
		default:
			return 2000; // 2s
	}
}

/**
 * 隐藏HTML级loading骨架屏
 */
function hideHtmlLoading(): void {
	try {
		const htmlLoading = document.getElementById('html-loading');
		if (htmlLoading) {
			// 添加隐藏类名，触发fadeout动画
			htmlLoading.classList.add('hidden');
			// 延迟后从DOM中移除
			setTimeout(() => {
				if (htmlLoading.parentNode) {
					htmlLoading.parentNode.removeChild(htmlLoading);
				}
			}, 300);
		}
	} catch (error) {
		console.warn('[useLoading] hideHtmlLoading error:', error);
	}
}

/** 等待首个路由渲染完成后移除 HTML 级 Loading。 */
function hideHtmlLoadingWhenReady(router: { isReady: () => Promise<unknown> }): void {
	router.isReady().then(() => {
		if (window.__htmlLoadingTimer) {
			clearTimeout(window.__htmlLoadingTimer);
			window.__htmlLoadingTimer = null;
		}

		requestAnimationFrame(hideHtmlLoading);
	});
}

// 创建Loading实例
function createLoadingInstance(options: LoadingProps = defaultProps) {
	// 合并默认配置
	const mergedDefaultProps = { ...defaultProps, ...options };

	// 使用全局单例状态
	const visible = computed(() => globalState.visible);

	const show = (params: LoadingProps | string = '加载中···') => {
		if (globalState.visible) return;

		// 清理定时器
		if (globalState.hideTimer) {
			clearTimeout(globalState.hideTimer);
			globalState.hideTimer = null;
		}
		if (globalState.showTimer) {
			clearTimeout(globalState.showTimer);
			globalState.showTimer = null;
		}

		// 设置属性
		globalState.props = typeof params === 'object' ? { ...mergedDefaultProps, ...params } : { ...mergedDefaultProps, tip: params };

		// 创建容器并渲染到 body
		if (!globalContainer) {
			globalContainer = document.createElement('div');
			globalContainer.id = 'loading-container';
			globalContainer.style = 'position:fixed;inset:0;z-index:1999;';
			document.body.appendChild(globalContainer);
		}

		// 设置固定定位样式
		(globalState.props as any).position = 'fixed';

		// 先挂载组件（visible=false状态）
		render(
			h(LoadingEl, {
				state: globalState, // 直接传入响应式对象，不解构
			}),
			globalContainer,
		);

		// 然后在下一个tick改变visible状态，触发进入动画
		nextTick(() => {
			if (globalState.props.delay) {
				globalState.showTimer = setTimeout(() => {
					globalState.visible = true;
					globalState.showTimer = null;
				}, globalState.props.delay);
			} else {
				globalState.visible = true;
			}
		});
	};

	const hide = (delay?: number) => {
		const hideDelay = typeof delay === 'number' ? delay : 0;

		// 清理定时器
		if (globalState.hideTimer) {
			clearTimeout(globalState.hideTimer);
			globalState.hideTimer = null;
		}
		if (globalState.showTimer) {
			clearTimeout(globalState.showTimer);
			globalState.showTimer = null;
		}

		const hideFn = () => {
			globalState.visible = false;
			globalState.hideTimer = null;
			// 延迟清理渲染
			setTimeout(() => {
				if (!globalState.visible && globalContainer) {
					render(null, globalContainer);
					globalContainer.remove();
					globalContainer = null;
				}
			}, 200);
		};

		if (hideDelay > 0) {
			globalState.hideTimer = setTimeout(hideFn, hideDelay);
		} else {
			hideFn();
		}
	};

	return {
    visible,
    showLoading: show,
    hideLoading: hide,
    hideHtmlLoading,
    getNetworkType,
    getLoadingTimeout,
    hideHtmlLoadingWhenReady
  };
}

/**
 * Loading composable
 * @param options 配置选项
 * @returns Loading 实例
 */
export function useLoading(options: LoadingProps = {}) {
	return createLoadingInstance(options);
}

// 导出辅助函数，供main.ts使用
export { hideHtmlLoading, hideHtmlLoadingWhenReady, getNetworkType, getLoadingTimeout };

export default useLoading;
